import MarkdownItVueLight from 'markdown-it-vue/dist/markdown-it-vue-light.umd.min.js';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import { mapState } from 'vuex';
import iconsMixin from '../dtwin/state/bridge/iconsMixin';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import devicesLimitMixin from './devicesLimitMixin';
export default {
  name: 'CoreVirtualDeviceAddWizard',
  components: {
    MarkdownItVueLight
  },
  mixins: [iconsMixin, devicesLimitMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    hideFields: {
      type: Array,
      required: false,
      default: () => []
    },
    modalTitle: {
      type: String,
      required: false,
      default: 'DigitalTwin.VirtualDevice.wizard.createTitle'
    },
    useDriver: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      testObj: null,
      accounts: [],
      deviceGroups: [],
      deviceTypes: [],
      deviceDriversData: this.useDriver ? {
        deviceDrivers: [],
        driverSearch: null,
        searchTerm: null,
        currentPage: 0,
        driversHasNextPage: true
      } : null,
      bridgeTypes: [],
      deviceBridgesForAccount: [],
      accountTemplatesData: {
        templates: [],
        currentPage: 1,
        hasNextPage: true
      },
      deviceBridgesLoading: false,
      selectedAccount: null,
      selectedBridge: null,
      selectedTemplate: null,
      searchExternalId: null,
      // on selecting an account, fetch the first twin of that account that the user has read-access to
      // used to prefill the twin-finder with a default-value
      defaultTwinId: null,
      deviceExistsWarning: null,
      useTemplate: false,
      parentDTwin: {
        title: '',
        id: ''
      },
      formValid: false,
      deviceObject: null,
      markdownPreview: false,
      newDeviceGroup: '',
      numberOfWmBusVirtualDevices: 0,
      wmbusLimitExceeded: false,
      lwm2mLimitExceeded: false,
      bridgeOfTemplateIsDeleted: false,
      // block the input fields while a create/import request is going on
      createServerRequest: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    borderColor() {
      return this.UiTheme ? 'rgba(255, 255, 255, 0.25)' : 'transparent';
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isHeaderDataSelected() {
      return this.selectedAccount && (this.selectedBridge || this.selectedTemplate);
    },
    requiredRule() {
      return v => !!v || this.$i18n_t('Common.fieldRequired');
    },
    bridgeCanRead() {
      if (!this.selectedBridge) {
        return false;
      }
      const bridgeTypeDef = this.bridgeTypes.find(bridgeDef => bridgeDef.type === this.selectedBridge.type);
      return get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read');
    },
    bridgeCreateParameters() {
      const createDeviceMethod = get(this.bridgeConfig(this.selectedBridge), 'manifest.functionScope.extension.devices.create');
      return get(this.bridgeConfig(this.selectedBridge), `manifest.commands.instance.${createDeviceMethod}.parameters.data.properties`) || {};
    },
    bridgeCreateFields() {
      const createParameters = this.bridgeCreateParameters;
      const bridgeDeviceData = get(this.deviceObject, 'properties.bridge.deviceData', {});
      let keys = Object.keys(createParameters).filter(key => {
        const visibleConfig = get(createParameters, `[${key}].visible`) || {};
        return Object.keys(visibleConfig).every(dependentKey => {
          return bridgeDeviceData[dependentKey] === visibleConfig[dependentKey];
        });
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(createParameters, `[${a}].orderIdx`) || 0;
        const orderIdxB = get(createParameters, `[${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    templateBridgeCreateFields() {
      return this.bridgeCreateFields.filter(key => !this.bridgeCreateParameters[key].predefineInTemplates);
    },
    templateCustomProperties() {
      return this.selectedTemplate.deviceMeta.customProperties;
    },
    templateReferenceIds() {
      return get(this.selectedTemplate, 'deviceMeta.referenceIds', {});
    },
    hasCustomProperties() {
      return Object.keys(this.deviceObject.data.keyValue || {}).length > 0;
    },
    hasReferenceIds() {
      return get(this.deviceObject, 'dynamical-data-routing-target', []).length > 0;
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    },
    templateParentObject() {
      return this.parentDTwin;
    },
    limits() {
      return {
        wmbus: this.wmbusLimitExceeded,
        lwm2m: this.lwm2mLimitExceeded
      };
    }
  },
  watch: {
    selectedAccount() {
      this.fetchBridges();
      // fetch only device-groups of vdevices that belong to this account
      this.fetchDeviceGroups();
      this.wmbusLimitExceeded = false;
      this.lwm2mLimitExceeded = false;
    },
    selectedBridge() {
      if (!this.selectedBridge) {
        return;
      }
      this.deviceObject = null;
      this.wmbusLimitExceeded = false;
      this.lwm2mLimitExceeded = false;
      if (this.selectedBridge.type === this.bridgeTypesMapping.wmbus) {
        this.checkLimits(this.selectedBridge.type, this.moduleKeys.wmbus, this.limitExceededFlags.wmbus);
      } else if (this.selectedBridge.type === this.bridgeTypesMapping.lwm2m) {
        this.checkLimits(this.selectedBridge.type, this.moduleKeys.lwm2m, this.limitExceededFlags.lwm2m);
      }
    },
    selectedTemplate(newTemplateValue) {
      if (!newTemplateValue) {
        return;
      }
      this.selectedBridge = this.deviceBridgesForAccount.find(bridge => bridge.id === newTemplateValue.deviceMeta.connectorId);
      if (!this.selectedBridge) {
        this.bridgeOfTemplateIsDeleted = true;
      }
      this.deviceGroups = this.deviceGroups.map(group => {
        return {
          text: group,
          value: group,
          disabled: this.selectedTemplate.deviceMeta.groups.includes(group)
        };
      });
      ApiService.digitalTwinGet(this, this.selectedTemplate.parentId).then(parentDTwin => {
        this.parentDTwin = parentDTwin;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('Common.digitalTwins.twinNotFound'), 'error');
        }
      });
    },
    'deviceDriversData.driverSearch': debounce(async function (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (newValue === '') {
        this.deviceObject.data.virtualDevice.deviceDriverId = null;
      }
      this.deviceDriversData = {
        deviceDrivers: [],
        driverSearch: newValue,
        searchTerm: newValue,
        currentPage: 0,
        driversHasNextPage: true
      };
      await this.fetchNextDrivers();
    }, 200),
    // https://digimondo.atlassian.net/browse/RD-5760
    // Autogenerate EndDevice ID when creating Things Stack from Device EUI
    // Do not autogenerate when importing a device
    'deviceObject.properties.bridge.deviceData.dev_eui': function (newEui) {
      const isDeviceImport = get(this.deviceObject, 'properties.bridge.isDeviceImport');
      const isThingsStack = get(this.deviceObject, 'properties.bridge.type') === 'bridge-thingsstack';
      if (isThingsStack && !isDeviceImport) {
        this.deviceObject.properties.bridge.deviceData.device_id = `eui-${newEui}`;
      }
    }
  },
  created() {
    this.fetchAccounts();
    this.fetchDeviceTypes();
  },
  methods: {
    get,
    closeDialog() {
      this.selectedAccount = null;
      this.selectedBridge = null;
      this.$emit('close');
    },
    applyBridgeCreateDeviceDefaultsFromManifest() {
      const bridgeTypeDef = this.bridgeConfig(this.selectedBridge);
      if (!bridgeTypeDef) {
        return;
      }
      const createDeviceMethod = get(bridgeTypeDef, 'manifest.functionScope.extension.devices.create');
      if (!createDeviceMethod) {
        return;
      }
      const properties = get(bridgeTypeDef, `manifest.commands.instance.${createDeviceMethod}.parameters.data.properties`, {});
      const dd = this.deviceObject.properties.bridge.deviceData;
      Object.keys(properties).forEach(key => {
        const def = properties[key].default;
        if (def !== undefined && dd[key] === undefined) {
          this.$set(dd, key, def);
        }
      });
    },
    deviceConfigChanged() {
      // force a UI-refresh of the fields (dependent config fields)
      this.deviceObject = cloneDeep(this.deviceObject);
      this.wmbusLimitExceeded = false;
      this.lwm2mLimitExceeded = false;
    },
    async endDriversIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        await this.fetchNextDrivers();
      }
    },
    async fetchNextDrivers() {
      if (!this.deviceDriversData.driversHasNextPage || this.deviceDriversData.isLoading) {
        return;
      }
      try {
        const DRIVER_PAGE_SIZE = 10;
        this.deviceDriversData.isLoading = true;
        const moreDrivers = await ApiService.virtualDeviceDrivers.fetch({
          fields: 'id,title',
          offset: this.deviceDriversData.currentPage,
          limit: DRIVER_PAGE_SIZE,
          search: this.deviceDriversData.searchTerm
        });
        this.deviceDriversData.currentPage += DRIVER_PAGE_SIZE;
        this.deviceDriversData.deviceDrivers = [...this.deviceDriversData.deviceDrivers, ...moreDrivers.deviceDrivers];
        if (this.deviceDriversData.deviceDrivers.length >= moreDrivers.totalCount) {
          this.deviceDriversData.driversHasNextPage = false;
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.wizard.fetchDriverError'), 'error');
      } finally {
        this.deviceDriversData.isLoading = false;
      }
    },
    fetchAccounts() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        // filter the root-tenant, because that one does not have a twin to attach the new device to
        this.accounts = accounts.filter(t => !t.isRootTenant);
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    fetchDeviceTypes() {
      NiotaClassicApiService.deviceTypesGetPlain().then(res => {
        this.deviceTypes = res.data.devicetypes;
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
        console.error(error);
      });
    },
    fetchDeviceGroups() {
      if (this.selectedAccount === null) {
        return;
      }
      ApiService.virtualDeviceFetchGroups(this, [this.selectedAccount]).then(res => {
        this.deviceGroups = res.groupList || [];
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
        console.error(error);
      });
    },
    // in case of an import, check if at the same account for the same bridge
    // there is a device with the same deviceId already
    findDeviceWithExternalId() {
      this.deviceExistsWarning = null;
      return ApiService.virtualDeviceFetch(this,
      // context
      null,
      // userId
      [],
      // scopeIds
      [{
        type: 'tenantIds',
        ids: [this.selectedAccount]
      }], null,
      // favourite
      null,
      // orderBy
      0,
      // offset
      20,
      // limit
      [{
        variable: 'deviceId',
        search: this.searchExternalId
      }, {
        variable: 'bridge.type',
        search: this.selectedBridge.type
      }]).then(res => {
        const exists = res.virtualDevices.find(d => {
          return d.properties.bridge.configId === this.selectedBridge.id;
        });
        this.deviceExistsWarning = exists;
        if (!exists) {
          return this.importExistingDevice();
        }
      }).catch(error => {
        console.error(error);
      });
    },
    async endTemplatesIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        await this.fetchNextTemplates();
      }
    },
    async fetchNextTemplates() {
      if (!this.accountTemplatesData.hasNextPage || this.accountTemplatesData.isLoading) {
        return;
      }
      try {
        const TEMPLATE_PAGE_SIZE = 10;
        this.accountTemplatesData.isLoading = true;
        const moreTemplates = await ApiService.deviceTemplatesFetch({
          tenantIds: [this.selectedAccount],
          page: this.accountTemplatesData.currentPage,
          pageSize: TEMPLATE_PAGE_SIZE
        });
        this.accountTemplatesData.currentPage++;
        const pageProductiveData = moreTemplates.data.map(td => td.ProductiveVersion);
        this.accountTemplatesData.templates = [...this.accountTemplatesData.templates, ...pageProductiveData];
        if (this.accountTemplatesData.templates.length >= moreTemplates.pagination['total-count']) {
          this.accountTemplatesData.hasNextPage = false;
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.fetchError'), 'error');
      } finally {
        this.accountTemplatesData.isLoading = false;
      }
    },
    fetchBridges() {
      this.deviceBridgesForAccount = [];
      this.accountTemplatesData = {
        templates: [],
        currentPage: 1,
        hasNextPage: true
      };
      this.selectedBridge = null;
      if (this.selectedAccount === null) {
        return;
      }
      this.deviceBridgesLoading = true;
      const dataPromises = [ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }), ApiService.getBridgesForTenant(this.selectedAccount),
      // fetch the first found twin for this account
      // is hopefully the root-twin
      ApiService.digitalTwinFetchChildren(this, null, 0, 1, null, ['bare'], false, this.selectedAccount)];
      Promise.all(dataPromises).then(results => {
        this.bridgeTypes = results[0];
        let validDeviceBridges = results[1].filter(bridgeInstance => {
          const bridgeTypeDef = results[0].find(bridgeDef => bridgeDef.type === bridgeInstance.type);
          // this bridge-type must have a create-functionality
          // otherwise it should have a read to get 1 single existing device as import
          if (get(bridgeTypeDef, 'manifest.functionScope.extension.devices.create') || get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read')) {
            return true;
          }
          return false;
        });
        this.deviceBridgesForAccount = validDeviceBridges.map(bridge => {
          return bridge.state === 'active' ? bridge : {
            ...bridge,
            name: `${bridge.name} (inactive)`,
            disabled: true
          };
        });
        this.defaultTwinId = get(results[2], 'digitaltwins[0].id', null);
        this.deviceBridgesLoading = false;
        this.fetchNextTemplates();
      }).catch(error => {
        this.deviceBridgesLoading = false;
        console.error('Error fetching bridges', error);
      });
    },
    bridgeConfig(bridge) {
      const foundMatch = this.bridgeTypes.find(config => {
        return config.type === bridge.type;
      });
      return foundMatch;
    },
    importExistingDevice() {
      const bridgeTypeDef = this.bridgeTypes.find(bridgeDef => bridgeDef.type === this.selectedBridge.type);
      const readMethod = get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read');
      const parameterNames = Object.keys(get(bridgeTypeDef, `manifest.commands.instance.${readMethod}.parameters`, {}));
      let parameters = {};
      if (parameterNames.length >= 1) {
        var _this$searchExternalI;
        parameters[parameterNames[0]] = bridgeTypeDef.type === 'bridge-lwm2m-server' ? this.searchExternalId : (_this$searchExternalI = this.searchExternalId) === null || _this$searchExternalI === void 0 ? void 0 : _this$searchExternalI.toLowerCase();
      }
      this.createServerRequest = true;
      ApiService.bridgeCommand(this.selectedBridge.id, readMethod, parameters).then(result => {
        if (!result.error && result.data) {
          this.createNewDevice(result.data);
          this.$emit('show-device-edit', this.deviceObject);
        } else {
          this.$snack(get(result, 'data.error') || result.errorMessage, 'error');
        }
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.createFailed'), 'error');
        }
      }).finally(() => {
        this.createServerRequest = false;
      });
    },
    createNewDevice(deviceData = undefined) {
      this.deviceObject = {
        parentId: this.defaultTwinId,
        data: {
          keyValue: {},
          virtualDevice: {},
          tags: []
        },
        properties: {
          groups: [],
          type: 'virtualDevice',
          bridge: {
            isDeviceImport: get(deviceData, 'device_id') !== undefined,
            type: this.selectedBridge.type,
            configId: this.selectedBridge.id,
            deviceData: deviceData || {}
          }
        },
        'dynamical-data-routing-target': []
      };
      if (this.useTemplate) {
        if (this.selectedTemplate.deviceMeta.connectorData) {
          this.deviceObject.properties.bridge.deviceData = {
            ...this.selectedTemplate.deviceMeta.connectorData,
            ...this.deviceObject.properties.bridge.deviceData
          };
        }
        this.applyBridgeCreateDeviceDefaultsFromManifest();
        this.deviceObject.data.tags.push(...this.selectedTemplate.deviceMeta.tags);
        this.deviceObject.properties.groups.push(...this.selectedTemplate.deviceMeta.groups);
        this.deviceObject.properties.groups.forEach(templateGroup => {
          if (!this.deviceGroups.includes(templateGroup)) {
            this.deviceGroups.push({
              text: templateGroup,
              value: templateGroup,
              disabled: true
            });
          }
        });
        this.deviceObject.deviceTemplateId = this.selectedTemplate.id;
        this.deviceObject.data.virtualDevice.deviceDriverId = this.selectedTemplate.deviceDriverId;
        const tempObj = {}; // v-model cannot bind keys for some reason when they are dynamically created, so temp object solved it
        Object.keys(this.templateCustomProperties).forEach(cpKey => {
          const defaultValue = this.templateCustomProperties[cpKey].defaultValue;
          tempObj[cpKey] = defaultValue ? defaultValue : undefined;
        });
        this.deviceObject.data.keyValue = tempObj;
        const tempArrayDdr = [];
        Object.keys(this.templateReferenceIds).forEach(key => {
          const defaultValue = this.templateReferenceIds[key].defaultValue;
          const obj = {
            [key]: defaultValue !== undefined ? defaultValue : ''
          };
          tempArrayDdr.push(obj);
        });
        this.deviceObject['dynamical-data-routing-target'] = tempArrayDdr;
      }
    },
    parentSelectorFilter(item) {
      return (
        // only allow new parents from the same tenant
        item.tenantId === this.selectedAccount
      );
    },
    setTags(tags) {
      this.deviceObject.data.tags = tags;
    },
    addNewDeviceGroup() {
      let matchingGroup = this.deviceGroups.find(g => {
        const group = this.useTemplate ? g.value : g;
        return group.toLowerCase().trim() === this.newDeviceGroup.toLowerCase().trim();
      });
      if (!matchingGroup) {
        matchingGroup = this.newDeviceGroup.trim();
        this.deviceGroups.push(this.useTemplate ? {
          text: matchingGroup,
          value: matchingGroup
        } : matchingGroup);
      }
      this.deviceObject.properties.groups.push(matchingGroup);
      this.newDeviceGroup = '';
    },
    save() {
      this.createServerRequest = true;
      ApiService.virtualDeviceCreate(this, this.deviceObject).then(res => {
        this.$emit('created', res);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.createSuccess'), 'success');
      }).catch(error => {
        if (get(error, 'response.data.data.data.errors')) {
          const fieldErrors = get(error, 'response.data.data.data.errors');
          const snackText = Object.keys(fieldErrors).map(field => {
            return `[${field}] ${fieldErrors[field]}`;
          });
          this.$snack(snackText.join('\n'), 'error');
        } else if (error.response && this.$i18n_te(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`, {
            errormessage: get(error, 'response.data.data.response.error') || get(error, 'response.data.data')
          }), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.createFailed'), 'error');
        }
      }).finally(() => {
        this.createServerRequest = false;
      });
    },
    getDependsOnFieldValue(createFields, currentField, deviceData) {
      const dependsOnFieldName = get(createFields[currentField], 'dependsOnField');
      if (!dependsOnFieldName) {
        return null;
      }
      return deviceData[dependsOnFieldName];
    },
    restrictCharacters(event) {
      if (event.key === ',' || event.key === ';') {
        event.preventDefault();
      }
    }
  }
};